// Pelaajat (perhe): pelaajan vaihto, profiilin muokkaus, retken kaverit ja Perhe-kortti.
//
// Jokaisella pelaajalla on oma ruutukartta, omat pisteet, merkit ja tukikohta
// (palvelimella oma tietokanta, ks. backend/app/family.py). Kaikki muu sovellus
// näyttää aina valitun pelaajan tiedot, joten tämä tiedosto hoitaa vain sen,
// kuka pelaa ja ketkä ovat retkellä mukana. Mikään ei riipu pelaajan iästä:
// kaikki valinnat tekee se, jolla puhelin on kädessä.

import { useEffect, useState } from "preact/hooks";

import { OWNER, EMBLEMS, companionNote, companionOptions, emblemEmoji, familyGroups, findPlayer, movePlan } from "../lib/players.js";
import { Sheet } from "./Sheet.jsx";

const COLORS = ["#b0137a", "#1f74b0", "#2f7d3b", "#d9822b", "#7b4fa3", "#c0392b", "#16837a", "#8a6d1d"];

/** Pelaajan hahmo värilliseltä pohjalta. Väri ei kanna merkitystä yksin: vieressä on aina nimi. */
export function PlayerChip({ player, large = false }) {
  return (
    <span class={`player-chip ${large ? "large" : ""}`} style={{ "--pc": player?.color ?? "var(--ink-soft)" }} aria-hidden="true">
      {emblemEmoji(player?.emblem)}
    </span>
  );
}

/** Kartan kulmassa: kuka pelaa nyt. Näkyy vain, kun pelaajia on enemmän kuin yksi. */
export function PlayerBadge({ app }) {
  const [open, setOpen] = useState(false);
  if (app.account || !app.players || app.players.length < 2 || !app.player) return null;
  return (
    <>
      <button class="player-badge" style={{ "--pc": app.player.color }} onClick={() => setOpen(true)} aria-label={`Pelaajana ${app.player.name}. Vaihda pelaajaa.`}>
        <PlayerChip player={app.player} />
        <span>{app.player.name}</span>
      </button>
      {open && <PlayerSwitchSheet app={app} onClose={() => setOpen(false)} />}
    </>
  );
}

/**
 * Ylläpitäjän lista muiden perheiden pelaajista (esim. kaverin itse luoma pelaaja). Heiksi ei voi vaihtaa,
 * mutta pelaajan voi siirtää omaan perheeseen ("Siirrä perheeseeni"), muokata tai poistaa. Tavalliselle
 * puhelimelle lista on tyhjä, eikä komponentti piirrä mitään.
 */
export function OtherPlayers({ app, onEdit }) {
  const [busy, setBusy] = useState(null);
  if (!app.otherPlayers?.length) return null;
  const join = async (p) => {
    setBusy(p.id);
    const ok = await app.setPlayerFamily(p.id, { join_with: OWNER }); // OWNER on aina pääperheessä
    setBusy(null);
    if (ok) app.toast({ kind: "info", what: `${p.name} on nyt perheessäsi` });
  };
  return (
    <>
      <p class="hint">Muut pelaajat palvelimella. Heiksi ei voi vaihtaa. Voit siirtää pelaajan omaan perheeseesi tai muokata tai poistaa hänet.</p>
      {app.otherPlayers.map((p) => (
        <div class="other-line" key={p.id}>
          <span class="player-opt"><PlayerChip player={p} /><b>{p.name}</b></span>
          <div class="other-actions">
            <button class="btn btn-rasti" disabled={busy === p.id} aria-label={`Siirrä pelaaja ${p.name} perheeseeni`} onClick={() => join(p)}>Siirrä perheeseeni</button>
            <button class="btn" aria-label={`Muokkaa pelaajaa ${p.name}`} onClick={() => onEdit(p)}>Muokkaa</button>
          </div>
        </div>
      ))}
    </>
  );
}

/**
 * Ylläpitäjä: siirtää minkä tahansa jo luodun pelaajan perheeseen (omaan, toisen perheeseen tai uuteen perheeseen).
 * Valinta tehdään listoista, joten kuka tahansa pelaaja kelpaa, ei vain tietty.
 */
export function MovePlayerSheet({ app, onClose }) {
  const players = app.players ?? [];
  const movable = players.filter((p) => !p.is_owner);
  const groups = familyGroups(players);
  const own = players.find((p) => p.is_owner)?.family ?? groups[0]?.id ?? "";
  const [who, setWho] = useState(movable[0]?.id ?? "");
  const [target, setTarget] = useState(own);
  const [busy, setBusy] = useState(false);
  const labelOf = (famId) => groups.find((g) => g.id === famId)?.label ?? "";
  const plan = movePlan(players, who, target);
  const current = players.find((p) => p.id === who);
  const save = async () => {
    if (!plan) return;
    setBusy(true);
    const ok = await app.setPlayerFamily(who, plan);
    setBusy(false);
    if (ok) {
      app.toast({ kind: "info", what: `${current?.name ?? "Pelaaja"} siirretty` });
      onClose();
    }
  };
  return (
    <Sheet title="Lisää pelaaja perheeseen" onClose={onClose}>
      {movable.length === 0 ? (
        <p class="hint">Palvelimella ei ole muita pelaajia, joita voisi siirtää.</p>
      ) : (
        <>
          <div class="field">
            <label for="move-who">Pelaaja</label>
            <select id="move-who" value={who} onChange={(e) => setWho(e.currentTarget.value)}>
              {movable.map((p) => <option key={p.id} value={p.id}>{p.name} (perhe: {labelOf(p.family)})</option>)}
            </select>
          </div>
          <div class="field">
            <label for="move-target">Perheeseen</label>
            <select id="move-target" value={target} onChange={(e) => setTarget(e.currentTarget.value)}>
              {groups.map((g) => <option key={g.id} value={g.id}>{g.id === own ? `Oma perheeni: ${g.label}` : g.label}</option>)}
              <option value="new">Uusi oma perhe</option>
            </select>
          </div>
          {!plan && current && target !== "new" && <p class="hint">{current.name} on jo tässä perheessä.</p>}
          <div class="sheet-actions">
            <button class="btn btn-rasti" disabled={!plan || busy} onClick={save}>Siirrä perheeseen</button>
            <button class="btn" onClick={onClose}>Peruuta</button>
          </div>
        </>
      )}
    </Sheet>
  );
}

/** Pelaajan vaihto. Kesken retken vaihto on estetty, jotta ruudut eivät mene väärälle pelaajalle. */
export function PlayerSwitchSheet({ app, onClose }) {
  const [editing, setEditing] = useState(undefined); // undefined = ei auki, null = uusi, objekti = muokattava
  const locked = Boolean(app.rec.active);
  return (
    <Sheet title="Kuka pelaa?" onClose={onClose}>
      {locked && <p class="hint">Pelaajaa ei voi vaihtaa kesken retken. Lopeta retki ensin.</p>}
      <div class="radio-list" role="radiogroup" aria-label="Pelaaja">
        {app.visiblePlayers.map((p) => (
          <div class="family-line" key={p.id}>
          <button
            role="radio"
            aria-checked={p.id === app.activePlayer}
            class="layer-opt player-opt"
            disabled={locked && p.id !== app.activePlayer}
            onClick={() => {
              app.switchPlayer(p.id);
              onClose();
            }}
          >
            <PlayerChip player={p} large />
            <b>{p.name}</b>
          </button>
          <button class="btn family-edit" aria-label={`Muokkaa pelaajaa ${p.name}`} onClick={() => setEditing(p)}>Muokkaa</button>
          </div>
        ))}
      </div>
      <OtherPlayers app={app} onEdit={setEditing} />
      <div class="sheet-actions">
        <button class="btn" onClick={() => setEditing(null)}>Lisää pelaaja</button>
        <button class="btn btn-quiet" onClick={() => setEditing(app.player)}>Muokkaa pelaajaa</button>
        <button class="btn" onClick={onClose}>Sulje</button>
      </div>
      {editing !== undefined && <PlayerEditSheet app={app} player={editing} onClose={() => setEditing(undefined)} />}
    </Sheet>
  );
}

/**
 * Ensikäynnistys: "Kuka sinä olet?". Ikkunaa ei voi sulkea ohittamalla, koska ohitus jättäisi
 * asentajan omistajaksi. Joko valitaan olemassa oleva pelaaja tai luodaan oma.
 */
export function WelcomeSheet({ app }) {
  const [creating, setCreating] = useState(false);
  const [admin, setAdmin] = useState(false);
  const [key, setKey] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const noop = () => {}; // ei suljettavissa taustaa napauttamalla, Esc:llä tai takaisin-painikkeella

  // Ylläpitäjä (omistaja) todistaa itsensä avaimella; sen jälkeen hän voi valita oman pelaajansa.
  const submitAdmin = async (e) => {
    e.preventDefault();
    setBusy(true);
    const problem = await app.enableAdmin(key);
    setBusy(false);
    setError(problem);
    if (!problem) setKey("");
  };

  return (
    <Sheet title="Tervetuloa!" onClose={noop}>
      <p class="hint">
        Jokaisella pelaajalla on omat ruudut, pisteet ja merkit. Luo itsellesi oma pelaaja, niin retkesi kertyvät sinulle.
        Toisen pelaajan tunnuksella ei voi pelata.
      </p>
      <div class="sheet-actions">
        <button class="btn btn-rasti btn-block" onClick={() => setCreating(true)}>Luo oma pelaaja</button>
        {!admin && <button class="btn btn-quiet" onClick={() => setAdmin(true)}>Olen ylläpitäjä</button>}
      </div>
      {admin && (
        <form onSubmit={submitAdmin}>
          <div class="field">
            <label for="welcome-admin-key">Ylläpitoavain</label>
            <input id="welcome-admin-key" type="password" autocomplete="off" autocapitalize="off" value={key} onInput={(e) => setKey(e.currentTarget.value)} />
          </div>
          {error && <p class="rec-note warn" role="alert">{error}</p>}
          <div class="sheet-actions">
            <button class="btn btn-block" type="submit" disabled={busy || !key.trim()}>Ota käyttöön</button>
          </div>
        </form>
      )}
      {creating && <PlayerEditSheet app={app} player={null} onClose={() => setCreating(false)} onSave={app.joinAsNewPlayer} />}
    </Sheet>
  );
}

/** Uuden pelaajan luonti tai olemassa olevan muokkaus ja poisto. */
export function PlayerEditSheet({ app, player, onClose, onSave = null }) {
  const isNew = !player;
  const [name, setName] = useState(player?.name ?? "");
  const [emblem, setEmblem] = useState(player?.emblem ?? EMBLEMS[(app.players?.length ?? 0) % EMBLEMS.length].id);
  const [color, setColor] = useState(player?.color ?? COLORS[(app.players?.length ?? 0) % COLORS.length]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [family, setFamily] = useState(player?.family ?? "");
  const groups = familyGroups(app.players);
  // Omistajan oletusnimi on "Minä", joka on kömpelö toisen pelaajan ruudulla ("tallentanut Minä").
  // Ensimmäistä toista pelaajaa lisättäessä kysytään siksi myös omistajan nimi.
  const owner = app.players?.find((p) => p.is_owner);
  // Vain omistajan omalla pelaajalla: toisen perheen pelaaja ei saa kysyä (eikä muuttaa) omistajan nimeä.
  const askOwnerName = isNew && !onSave && owner?.name === "Minä" && app.activePlayer === owner.id;
  const [ownerName, setOwnerName] = useState("");

  const save = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    const body = { name: name.trim(), emblem, color };
    // onSave: ensikäynnistyksen oma luonti, joka myös vaihtaa uuteen pelaajaan.
    let ok = onSave ? await onSave(body) : await app.savePlayer(player?.id ?? null, body, askOwnerName ? ownerName.trim() : "");
    // Perheen vaihto on ylläpitäjän päätös, ja se tallennetaan vain, jos se muuttui.
    if (ok && !isNew && app.admin && !player.is_owner && family !== player.family) {
      const target = groups.find((g) => g.id === family);
      ok = await app.setPlayerFamily(player.id, family === "new" || !target ? { new_family: true } : { join_with: target.members[0].id });
    }
    setBusy(false);
    if (ok) onClose();
  };

  return (
    <Sheet title={isNew ? "Uusi pelaaja" : "Muokkaa pelaajaa"} onClose={onClose} labelledBy="player-edit-title">
      <form onSubmit={save}>
        <div class="field">
          <label for="player-name">Nimi</label>
          <input id="player-name" value={name} maxLength={40} autoComplete="off" onInput={(e) => setName(e.currentTarget.value)} />
        </div>
        {askOwnerName && (
          <div class="field">
            <label for="owner-name">Sinun nimesi</label>
            <input id="owner-name" value={ownerName} maxLength={40} autoComplete="off" placeholder="Näkyy retkillä, jotka tallennat" onInput={(e) => setOwnerName(e.currentTarget.value)} />
          </div>
        )}
        <div class="field">
          <span class="label" id="emblem-label">Hahmo</span>
          <div class="emblem-grid" role="radiogroup" aria-labelledby="emblem-label">
            {EMBLEMS.map((e) => (
              <button type="button" key={e.id} class="emblem-opt" role="radio" aria-checked={emblem === e.id} aria-label={e.name} onClick={() => setEmblem(e.id)}>
                {e.emoji}
              </button>
            ))}
          </div>
        </div>
        <div class="field">
          <span class="label" id="color-label">Väri kartalla</span>
          <div class="color-grid" role="radiogroup" aria-labelledby="color-label">
            {COLORS.map((c) => (
              <button type="button" key={c} class="color-opt" role="radio" aria-checked={color === c} aria-label={`Väri ${c}`} style={{ "--pc": c }} onClick={() => setColor(c)} />
            ))}
          </div>
        </div>
        {!isNew && app.admin && !player.is_owner && (
          <div class="field">
            <label for="player-family">Perhe</label>
            <select id="player-family" value={family} onChange={(e) => setFamily(e.currentTarget.value)}>
              {groups.map((g) => <option key={g.id} value={g.id}>{g.label}</option>)}
              <option value="new">Uusi oma perhe</option>
            </select>
            <p class="s">Perheen vaihto ei poista pelaajaa: pisteet ja ruudut säilyvät. Pelaajan poisto on erillinen painike alla.</p>
          </div>
        )}
        <div class="sheet-actions">
          <button class="btn btn-rasti btn-block" type="submit" disabled={busy || !name.trim()}>{isNew ? "Lisää pelaaja" : "Tallenna"}</button>
          {!isNew && !player.is_owner && (
            <button class="btn btn-danger btn-block" type="button" onClick={() => setConfirmDelete(true)}>Poista pelaaja pysyvästi</button>
          )}
          <button class="btn btn-block" type="button" onClick={onClose}>Peruuta</button>
        </div>
      </form>
      {confirmDelete && (
        <Sheet title={`Poistetaanko ${player.name}?`} onClose={() => setConfirmDelete(false)} labelledBy="player-delete-title">
          <p class="hint">
            Pelaajan kaikki ruudut, pisteet, merkit ja tukikohta poistuvat pysyvästi. Sinulle kopioidut retket jäävät omiksi retkiksesi.
          </p>
          <div class="sheet-actions">
            <button class="btn btn-danger btn-block" onClick={async () => {
              const ok = await app.removePlayer(player.id);
              if (ok) { setConfirmDelete(false); onClose(); }
            }}>Poista pelaaja</button>
            <button class="btn btn-block" onClick={() => setConfirmDelete(false)}>Säilytä</button>
          </div>
        </Sheet>
      )}
    </Sheet>
  );
}

/** Retken kaverit: napautettavat hahmot. Kaverit saavat retkestä samat ruudut ja pisteet. */
export function CompanionPicker({ app, value, onChange, label = true }) {
  if (app.account) return null;
  const options = companionOptions(app.visiblePlayers, app.activePlayer); // vain pelaajat, jotka tämä puhelin saa valita
  if (options.length === 0) return null;
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  const note = companionNote(options, value);
  const allChosen = options.every((p) => value.includes(p.id));
  return (
    <div class="companions">
      <div class={label ? "companions-title" : "sr-only"} id="companions-label">Ketkä ovat mukana?</div>
      <div class="chips" role="group" aria-labelledby="companions-label">
        {options.map((p) => (
          <button key={p.id} class="chip player-pick" role="checkbox" aria-checked={value.includes(p.id)} style={{ "--pc": p.color }} onClick={() => toggle(p.id)}>
            <PlayerChip player={p} />
            {p.name}
          </button>
        ))}
        {options.length > 1 && (
          <button class="btn btn-quiet" onClick={() => onChange(allChosen ? [] : options.map((p) => p.id))}>
            {allChosen ? "Tyhjennä" : "Kaikki mukaan"}
          </button>
        )}
      </div>
      {/* role=status: ruudunlukija kertoo muutoksen, kun valintaa napautetaan. */}
      <p class={`companions-note ${note.alone ? "alone" : ""}`} role="status">{note.text}</p>
    </div>
  );
}

/** Päiväkirjan retken tiedot: kuka tallensi ja ketkä olivat mukana. */
export function TripPeople({ app, trip }) {
  const [editing, setEditing] = useState(false);
  const [chosen, setChosen] = useState(trip.companions ?? []);
  useEffect(() => setChosen(trip.companions ?? []), [trip.id, trip.companions?.join(",")]);

  if (trip.origin_player) {
    const origin = findPlayer(app.players, trip.origin_player);
    return (
      <div class="notice" role="note">
        Tämän retken on tallentanut {origin?.name ?? "toinen pelaaja"}. Ruudut ja pisteet on laskettu sinulle erikseen.
        Retken tietoja voi muuttaa vain {origin?.name ?? "tallentaja"}.
      </div>
    );
  }
  if (!app.players || app.players.length < 2) return null;
  const names = (trip.companions ?? []).map((id) => findPlayer(app.players, id)).filter(Boolean);
  return (
    <>
      <p class="s trip-people">
        {names.length ? (
          <>Mukana: {names.map((p) => <span key={p.id} class="people-tag"><PlayerChip player={p} /> {p.name}</span>)}</>
        ) : (
          "Ei kavereita mukana."
        )}{" "}
        {trip.status === "finished" && <button class="btn btn-quiet" onClick={() => setEditing(true)}>Muuta</button>}
      </p>
      {editing && (
        <Sheet title="Ketkä olivat mukana?" onClose={() => setEditing(false)}>
          <p class="hint">Uudet kaverit saavat retken ruudut ja pisteet. Poistetun kaverin pisteet oikaistaan hänen pistehistoriassaan.</p>
          <CompanionPicker app={app} value={chosen} onChange={setChosen} label={false} />
          <div class="sheet-actions">
            <button class="btn btn-primary btn-block" onClick={async () => {
              const ok = await app.setTripCompanions(trip.id, chosen);
              if (ok) setEditing(false);
            }}>Tallenna</button>
            <button class="btn btn-block" onClick={() => setEditing(false)}>Peruuta</button>
          </div>
        </Sheet>
      )}
    </>
  );
}

/** Passin Perhe-kortti: pelaajat vierekkäin ja yhteiset luvut. */
export function FamilyCard({ app }) {
  const [data, setData] = useState(null);
  const [adding, setAdding] = useState(false);
  const [moving, setMoving] = useState(false);
  const [editing, setEditing] = useState(null); // muokattava pelaaja (olio) tai null
  useEffect(() => {
    app.api.family().then(setData, () => setData(null));
  }, [app.dataVersion, app.players?.length]);

  // Vain tämän puhelimen sallimat pelaajat: muiden tunnuksella ei voi pelata.
  const shown = data ? data.players.filter((p) => app.visiblePlayers?.some((v) => v.id === p.id)) : null;
  const many = (shown ? shown.length : app.visiblePlayers?.length ?? 0) > 1;
  return (
    <section class="family-card" aria-label="Perhe">
      <h2>{many ? "Perhe" : "Pelaa yhdessä"}</h2>
      {!many && (
        <>
          <p class="s">
            Lisää lapsi tai kaveri pelaajaksi. Retkellä mukana olevat saavat ruudut ja pisteet, ja jokaisella on oma kartta,
            omat merkit ja oma tukikohta.
          </p>
          <button class="btn" onClick={() => setAdding(true)}>Lisää pelaaja</button>
        </>
      )}
      {many && shown && (
        <>
          <div class="family-list" role="radiogroup" aria-label="Pelaaja">
            {shown.map((p) => (
              <div class="family-line" key={p.id}>
                <button
                  class="family-row"
                  role="radio"
                  aria-checked={p.id === app.activePlayer}
                  style={{ "--pc": p.color }}
                  disabled={Boolean(app.rec.active) && p.id !== app.activePlayer}
                  onClick={() => app.switchPlayer(p.id)}
                >
                  <PlayerChip player={p} large />
                  <div>
                    <b>{p.name}</b>
                    <div class="s">
                      Taso {p.stats.level} · {p.stats.points.toLocaleString("fi-FI")} pistettä · {p.stats.small_tiles} ruutua
                      {p.square > 1 ? ` · neliö ${p.square} × ${p.square}` : ""}
                    </div>
                  </div>
                </button>
                {/* Muokkaus suoraan riviltä: ei tarvitse ensin valita pelaajaa. */}
                <button class="btn family-edit" aria-label={`Muokkaa pelaajaa ${p.name}`} onClick={() => setEditing(p)}>Muokkaa</button>
              </div>
            ))}
          </div>
          <div class="family-total">
            <div><b class="num">{data.family.tiles}</b><span>ruutua yhdessä</span></div>
            <div>
              <b class="num">{data.family.square ? `${data.family.square.size} × ${data.family.square.size}` : "–"}</b>
              <span>perheen maksimineliö</span>
            </div>
            <div><b class="num">{data.family.cluster?.size ?? 0}</b><span>perheen klusteri</span></div>
          </div>
          {data.family.shared_tiles > 0 && <p class="s">Yhteisiä ruutuja: {data.family.shared_tiles}.</p>}
          <div class="sheet-actions">
            <button class="btn" onClick={() => setAdding(true)}>Lisää pelaaja</button>
          </div>
        </>
      )}
      {app.admin && <button class="btn btn-block" style={{ marginTop: "10px" }} onClick={() => setMoving(true)}>Lisää olemassa oleva pelaaja perheeseen</button>}
      <OtherPlayers app={app} onEdit={setEditing} />
      {moving && <MovePlayerSheet app={app} onClose={() => setMoving(false)} />}
      {adding && <PlayerEditSheet app={app} player={null} onClose={() => setAdding(false)} />}
      {editing && <PlayerEditSheet app={app} player={editing} onClose={() => setEditing(null)} />}
    </section>
  );
}
